/**
 * Calculates the Levenshtein distance between two strings
 */
export const getLevenshteinDistance = (a, b) => {
  const matrix = [];
  for (let i = 0; i <= b.length; i++) matrix[i] = [i];
  for (let j = 0; j <= a.length; j++) matrix[0][j] = j;

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          Math.min(matrix[i][j - 1] + 1, matrix[i - 1][j] + 1),
        );
      }
    }
  }
  return matrix[b.length][a.length];
};

/**
 * Normalizes a name for fuzzy matching
 */
export const normalizeForFuzzy = (name) => {
  if (!name) return "";
  let val = name.toLowerCase();
  const suffixes = [
    "pvt",
    "ltd",
    "limited",
    "pharmaceuticals",
    "pharma",
    "labs",
    "laboratory",
    "laboratories",
    "healthcare",
    "health",
    "med",
    "medical",
    "associates",
    "associatas",
    "agency",
    "agencies",
    "enterprise",
    "enterprises",
  ];

  // Remove suffixes
  suffixes.forEach((s) => {
    const reg = new RegExp("\\b" + s + "\\b", "gi");
    val = val.replace(reg, "");
  });

  // Remove non-alphanumeric
  val = val.replace(/[^a-z0-9\s]/g, "");

  // Tokenize, Sort, and Join (handles "Sun Pharma" vs "Pharma Sun")
  let tokens = val.split(/\s+/).filter((t) => t.length > 0);
  tokens.sort();
  return tokens.join("");
};

/**
 * Checks if two strings are a close match (typo tolerant)
 */
export const isCloseMatch = (input, target) => {
  if (!input || !target) return false;

  const s1 = input.toLowerCase().trim();
  const s2 = target.toLowerCase().trim();

  // 1. Structural Match (Indifferent to spaces/order)
  const n1 = normalizeForFuzzy(s1);
  const n2 = normalizeForFuzzy(s2);

  if (n1 === n2 || n1.includes(n2) || n2.includes(n1)) return true;

  // 2. Global Typo Tolerance (for concatenated strings like "phramasun")
  if (n1.length > 3 && n2.length > 3) {
    const globalDist = getLevenshteinDistance(n1, n2);
    if (globalDist <= (n1.length > 7 ? 2 : 1)) return true;
  }

  // 3. Word-Level Typo Tolerance
  const words1 = s1.split(/\s+/);
  const words2 = s2.split(/\s+/);

  return words1.some((w1) => {
    if (w1.length < 3) return false;
    return words2.some((w2) => {
      if (w2.length < 3) return false;
      const distance = getLevenshteinDistance(w1, w2);
      const threshold = w2.length > 6 ? 2 : 1;
      return distance <= threshold;
    });
  });
};
